import React from 'react';
import Card from '../Card';
import EmptyStateCard from '../EmptyStateCard';
import Button from '../Button';
import { Task } from '../../types/task';
import { Project } from '../../types/project';
import { User } from '../../types/user';

interface IssuesTabProps {
  overdueTasks: Task[];
  unassignedTasks: Task[];
  projectsWithoutTasks: Project[];
  overloadedUsers: Array<{
    userId: string;
    userName: string;
    taskCount: number;
    overdueCount: number;
  }>;
  projects: Project[];
  users: User[];
  onRescheduleTask: (task: Task) => void;
}

const IssuesTab: React.FC<IssuesTabProps> = ({
  overdueTasks,
  unassignedTasks,
  projectsWithoutTasks,
  overloadedUsers,
  projects,
  users,
  onRescheduleTask
}) => {
  const summaryCards = [
    {
      value: overdueTasks.length,
      label: 'Overdue Tasks',
      borderColor: 'border-l-red-500',
      textColor: 'text-error'
    },
    {
      value: unassignedTasks.length,
      label: 'Unassigned Tasks',
      borderColor: 'border-l-amber-500',
      textColor: 'text-warning'
    },
    {
      value: projectsWithoutTasks.length,
      label: 'Empty Projects',
      borderColor: 'border-l-gray-400',
      textColor: 'text-gray-700 dark:text-gray-200'
    },
    {
      value: overloadedUsers.length,
      label: 'Overloaded Users',
      borderColor: 'border-l-red-500',
      textColor: 'text-error'
    }
  ];

  const hasNoIssues = overdueTasks.length === 0 && 
                      unassignedTasks.length === 0 && 
                      projectsWithoutTasks.length === 0 && 
                      overloadedUsers.length === 0;

  return (
    <div>
      <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-6">Issues & Risks</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {summaryCards.map((card, index) => (
          <Card key={index} className={`border-l-4 ${card.borderColor} bg-white dark:bg-gray-800`}>
            <Card.Body>
              <div className={`text-3xl font-bold ${card.textColor}`}>
                {card.value}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                {card.label}
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>

      {overdueTasks.length > 0 && (
        <Card className="mb-6">
          <Card.Body>
            <h3 className="text-lg font-semibold text-error mb-4">
              ⚠️ Overdue Tasks ({overdueTasks.length})
            </h3>
            {overdueTasks.map(task => {
              const project = projects.find(p => p.id === task.project_id);
              const assignedUser = task.assigned_to ? users.find(u => u.id === task.assigned_to) : null;
              const daysOverdue = Math.ceil(
                (new Date().getTime() - new Date(task.due_date!).getTime()) / (1000 * 60 * 60 * 24)
              );

              return (
                <div 
                  key={task.id} 
                  className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg text-red-900 dark:text-red-100 mb-2 flex justify-between items-center"
                >
                  <div className="flex-1">
                    <div className="font-bold text-gray-900 dark:text-gray-100">{task.title}</div>
                    <div className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                      Project: {project?.name || 'Unknown'} | 
                      Assigned to: {assignedUser ? (assignedUser.full_name || assignedUser.username) : 'Unassigned'} | 
                      <span className="text-error font-bold"> {daysOverdue} days overdue</span>
                    </div>
                  </div>
                  <Button 
                    onClick={() => onRescheduleTask(task)}
                    variant="primary"
                    size="sm"
                    className="ml-4"
                  >
                    Reschedule
                  </Button>
                </div>
              );
            })}
          </Card.Body>
        </Card>
      )}

      {unassignedTasks.length > 0 && (
        <Card className="mb-6">
          <Card.Body>
            <h3 className="text-lg font-semibold text-warning mb-4">
              📋 Unassigned Tasks ({unassignedTasks.length})
            </h3>
            {unassignedTasks.map(task => {
              const project = projects.find(p => p.id === task.project_id);
              return (
                <div 
                  key={task.id} 
                  className="p-3 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 dark:border-amber-700 rounded-lg mb-2"
                >
                  <div className="font-bold text-gray-900 dark:text-gray-100">{task.title}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Project: {project?.name || 'Unknown'} | Priority: {task.priority}
                  </div>
                </div>
              );
            })}
          </Card.Body>
        </Card>
      )}

      {projectsWithoutTasks.length > 0 && (
        <Card className="mb-6">
          <Card.Body>
            <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-4">
              📁 Projects Without Tasks ({projectsWithoutTasks.length})
            </h3>
            {projectsWithoutTasks.map(project => (
              <div 
                key={project.id} 
                className="p-3 bg-gray-50 dark:bg-gray-800/50 border-l-4 border-gray-400 dark:border-gray-600 rounded-lg mb-2"
              >
                <div className="font-bold text-gray-900 dark:text-gray-100">{project.name}</div>
                {project.description && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {project.description}
                  </div>
                )}
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      {overloadedUsers.length > 0 && (
        <Card>
          <Card.Body>
            <h3 className="text-lg font-semibold text-error mb-2">
              👥 Overloaded Resources ({overloadedUsers.length})
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Users with more than 5 active tasks
            </p>
            {overloadedUsers.map(user => (
              <div 
                key={user.userId} 
                className="p-3 bg-red-50 dark:bg-red-900/20 border-l-4 border-error/30 rounded-lg mb-2 flex justify-between items-center"
              >
                <div>
                  <div className="font-bold text-gray-900 dark:text-gray-100">{user.userName}</div>
                  {user.overdueCount > 0 && (
                    <div className="text-sm text-error mt-1">
                      {user.overdueCount} overdue task{user.overdueCount > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
                <div className="px-2 py-1 text-xs font-semibold rounded-full bg-error/15 text-error">
                  {user.taskCount} tasks
                </div>
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      {hasNoIssues && (
        <EmptyStateCard
          icon="✅"
          title="No Issues Found!"
          message="All projects, tasks, and resources are in good shape."
        />
      )}
    </div>
  );
};

export default IssuesTab;
